<template>
  <td class="c-email-template__column" style="padding: 0" :style="rootStyles">
    <div :style="containerStyles">
      <table
        border="0"
        cellpadding="0"
        cellspacing="0"
        role="presentation"
        width="100%"
      >
        <tbody>
          <tr>
            <td :style="contentStyles">
              <div :data-container-column="id" style="position: relative">
                <ak-draggable
                  v-model="childrenData"
                  item-key="uuid"
                  group="children"
                  handle="[data-draggable-child-handle]"
                  class="email-template__content-container"
                >
                  <template #item="{ element, index }">
                    <div
                      :key="index"
                      style="position: relative"
                      :data-container-content="`${id}-${index}`"
                      :style="builderElementStyles(index, element.type)"
                    >
                      <ak-attached-action
                        v-if="isBuilder"
                        position="left"
                        has-shadow
                        :alignment="
                          element.type === 'divider' ? 'center' : 'start'
                        "
                        :container="`[data-container-content='${id}-${index}']`"
                      >
                        <ak-button
                          circle
                          size="small"
                          color="white"
                          class="has-shadow-large"
                          data-draggable-child-handle
                        >
                          <ak-icon type="drag" />
                        </ak-button>
                      </ak-attached-action>
                      <ak-attached-action
                        v-if="isBuilder"
                        has-shadow
                        :alignment="
                          element.type === 'divider' ? 'center' : 'start'
                        "
                        :container="`[data-container-content='${index}']`"
                      >
                        <ak-button
                          circle
                          size="small"
                          color="white"
                          class="has-shadow-large"
                          @click.prevent="removeContent(element, index)"
                        >
                          <ak-icon type="trash-b" color="danger" />
                        </ak-button>
                      </ak-attached-action>
                      <table
                        border="0"
                        cellpadding="0"
                        cellspacing="0"
                        role="presentation"
                        width="100%"
                      >
                        <ak-email-template-content
                          :model-value="element"
                          :block-index="blockIndex"
                          :settings="settings"
                          :is-builder="isBuilder"
                          :is-last="index === childrenData.length - 1"
                          :is-flush="blockSettings.flushSpacing"
                          :content-index="index"
                          :width="innerWidth"
                          :drawer-container="drawerContainer"
                        />
                      </table>
                    </div>
                  </template>
                </ak-draggable>
                <ak-attached-action
                  v-if="isBuilder"
                  :container="`[data-container-column='${id}']`"
                  class="ak-attached-actions__item--overlap"
                  position="bottom"
                >
                  <ak-popover
                    v-if="isBuilder"
                    theme="dark"
                    placement="top"
                    overlay
                  >
                    <template #trigger>
                      <ak-button circle>
                        <ak-icon type="plus" />
                      </ak-button>
                    </template>
                    <template #popover="props">
                      <ak-action-grid class="width-250">
                        <ak-action-grid-item
                          icon="font"
                          @click.prevent="addTitle(props.close)"
                        >
                          Title
                        </ak-action-grid-item>
                        <ak-action-grid-item
                          icon="align-left"
                          @click.prevent="addText(props.close)"
                        >
                          Text
                        </ak-action-grid-item>
                        <ak-action-grid-item
                          icon="spacer"
                          @click.prevent="addDivider(props.close)"
                        >
                          Divider
                        </ak-action-grid-item>
                        <ak-action-grid-item
                          icon="image"
                          @click.prevent="addImage(props.close)"
                        >
                          Image
                        </ak-action-grid-item>
                        <ak-action-grid-item
                          icon="button"
                          @click.prevent="addButton(props.close)"
                        >
                          Button
                        </ak-action-grid-item>
                        <ak-action-grid-item
                          icon="code"
                          @click.prevent="addHtml(props.close)"
                        >
                          HTML
                        </ak-action-grid-item>
                        <ak-action-grid-item
                          icon="edit"
                          @click.prevent="addSignature(props.close)"
                        >
                          Signature
                        </ak-action-grid-item>
                      </ak-action-grid>
                    </template>
                  </ak-popover>
                </ak-attached-action>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </td>
</template>

<script>
import { defineComponent, ref, computed, watch, onMounted } from 'vue'
import config from '../../utils/config'
import store from '@akerolabs/akero-vue/src/store/index'
import { get } from '@akerolabs/akero-vue/src/utils/request'
import Draggable from '@akerolabs/akero-vue/src/components/draggable/Draggable'
import Popover from '@akerolabs/akero-vue/src/components/popover/Popover.vue'
import AttachedAction from '@akerolabs/akero-vue/src/components/attachedAction/AttachedAction.vue'
import ActionGrid from '@akerolabs/akero-vue/src/components/actionGrid/ActionGrid.vue'
import ActionGridItem from '@akerolabs/akero-vue/src/components/actionGrid/ActionGridItem.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import Icon from '@akerolabs/akero-vue/src/components/icon/Icon.vue'
import Content from '../content/Content.vue'
import { SnackbarProgrammatic } from '@akerolabs/akero-vue/src/components/snackbar'

import {
  newTitle,
  newImage,
  newText,
  newDivider,
  newButton,
  newHTML,
  newSignature,
} from '../../utils/helpers'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-email-template-column></ak-email-template-column>
   */
  name: 'AkEmailTemplateColumn',

  /**
   * @type {Boolean}
   * @description Causes the vue application to preserve HTML comments
   */
  comments: true,

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [Draggable.name]: Draggable,
    [Content.name]: Content,
    [Popover.name]: Popover,
    [Button.name]: Button,
    [Icon.name]: Icon,
    [AttachedAction.name]: AttachedAction,
    [ActionGrid.name]: ActionGrid,
    [ActionGridItem.name]: ActionGridItem,
  },

  /**
   * @type {Boolean}
   * @description This prevents all element attributes that are not
   * defined in the props object from being applied to the rendered
   * component root element, the exception to this is "class" all
   * of the given classes would be merged with the component
   * defined ones.
   */
  inheritAttrs: false,

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Object}
     * @description The column that we want to render/edit
     */
    children: {
      type: Array,
      default: () => [],
    },

    /**
     * @type {Number}
     * @description Block index
     */
    blockIndex: {
      type: Number,
      default: 0,
    },

    /**
     * @type {Number}
     * @description Determines the solumn spacing value
     */
    columnSpacing: {
      type: Number,
      default: 10,
    },

    /**
     * @type {Number}
     * @description The index of the column, used in the multi column
     * blocks.
     */
    columnIndex: {
      type: Number,
      default: undefined,
    },

    /**
     * @type {Function}
     * @description A method to update the block content array when
     * the child components of this are dragged around
     */
    updateColumn: {
      type: Function,
      default: () => {},
    },

    /**
     * @type {Number}
     * @description The width of the column as decimal
     */
    widthDecimal: {
      type: Number,
      default: 1,
    },

    /**
     * @type {Object}
     * @description The settings for the template
     */
    settings: {
      type: Object,
      default: () => ({ ...config.defaultTemplateStyles }),
    },

    /**
     * @type {Object}
     * @description The settings for the template
     */
    opts: {
      type: Object,
      default: () => ({}),
    },

    /**
     * @type {Object}
     * @description The settings for the block
     */
    blockSettings: {
      type: Object,
      default: () => {},
    },

    /**
     * @type {Boolean}
     * @description Determines if we are to render the block for the
     * builder component (drag and drop editor)
     */
    isBuilder: {
      type: Boolean,
      default: false,
    },

    width: {
      type: Number,
      default: 600,
    },

    /**
     * @type {String}
     * @description The container in which to position the drawers
     */
    drawerContainer: {
      type: String,
      default: null,
    },
  },

  setup(props) {
    const childrenData = ref(props.children)
    const showEmptyState = ref(true)
    const hover = ref(false)
    const id = ref(null)

    /**
     * @returns {Object}
     * @description returns the root styles for the component
     */
    const rootStyles = computed(() => {
      return {
        width: `${columnWidth.value}px`,
        'vertical-align':
          props.blockSettings && props.blockSettings.columnAlignment,
      }
    })

    /**
     * @returns {Object}
     * @description returns the container styles for the wrapper
     */
    const containerStyles = computed(() => {
      return {
        textAlign: 'left',
        direction: 'ltr',
        width: '100%',
      }
    })

    /**
     * @returns {Object}
     * @description returns the content styles for the component
     */
    const contentStyles = computed(() => {
      return {
        verticalAlign: 'top',
        padding: `0 ${props.columnSpacing}px ${props.columnSpacing}px`,
      }
    })

    /**
     * @returns Number
     * @description Returns the inner width of the column, for images etc
     */
    const columnWidth = computed(() => {
      const baseWidth = 600 - props.columnSpacing - props.columnSpacing
      const colWidth = baseWidth * props.widthDecimal
      return Math.round(colWidth)
    })

    /**
     * @returns Number
     * @description Returns the inner width of the column, for images etc
     */
    const innerWidth = computed(() => {
      const edgeSpace = props.columnSpacing * 2
      const innerWidth = columnWidth.value - edgeSpace
      return innerWidth
    })

    /**
     * @description Adds a new title child block to the column
     * @param {Function} Close - closes the popover
     */
    const addTitle = (close) => {
      childrenData.value.push(newTitle())
      if (close) close()
    }

    /**
     * @description Adds a new text child block to the column
     * @param {Function} Close - closes the popover
     */
    const addText = (close) => {
      childrenData.value.push(newText())
      if (close) close()
    }

    /**
     * @description Adds a new divider child block to the column
     * @param {Function} Close - closes the popover
     */
    const addDivider = (close) => {
      childrenData.value.push(newDivider())
      if (close) close()
    }

    /**
     * @description Adds a new image child block to the column
     * @param {Function} Close - closes the popover
     */
    const addImage = (close) => {
      childrenData.value.push(newImage())
      if (close) close()
    }

    /**
     * @description Adds a new button child block to the column
     * @param {Function} Close - closes the popover
     */
    const addButton = (close) => {
      childrenData.value.push(newButton())
      if (close) close()
    }

    /**
     * @description Adds a new html child block to the column
     * @param {Function} Close - closes the popover
     */
    const addHtml = (close) => {
      childrenData.value.push(newHTML())
      if (close) close()
    }

    /**
     * @description Adds a new signature child block to the column
     * @param {Function} Close - closes the popover
     */
    const addSignature = async (close) => {
      const user = await get('/users')
      const accountID = store.getters['session/account']

      console.log(get, user, accountID)
      console.log(JSON.stringify(props.opts))
      childrenData.value.push(newSignature())
      if (close) close()
    }

    /**
     * @param {Object} block
     * @description Removes content the childrenData array.
     */
    const removeContent = (content) => {
      const prevData = childrenData.value

      childrenData.value = childrenData.value.filter(
        (i) => i.uuid !== content.uuid,
      )

      SnackbarProgrammatic.open({
        message: 'Content removed',
        type: 'primary',
        position: 'bottom',
        actionText: 'Undo',
        duration: 3000,
        inverse: true,
        onAction: () => {
          childrenData.value = prevData
        },
      })
    }

    /**
     * @param {Object} contentIndex
     * @param {Object} type
     * @returns {Object}
     * @description Convert paddings to margins for the builder, to avoid awkward
     * space whilst dragging this is converted to paddings on the internal TD
     * on the rendered version
     */
    const builderElementStyles = (contentIndex, type) => {
      const isFlush = props.blockSettings.flushSpacing
      if (props.isBuilder) {
        const isLast = contentIndex === childrenData.value.length - 1
        const isDivider = type === 'divider'
        let mb = isLast ? 0 : '10px'
        if (isDivider) {
          mb = '-10px'
        }
        return {
          marginTop: isFlush && contentIndex === 0 ? 0 : '10px',
          marginBottom: mb,
        }
      }
      return null
    }

    onMounted(() => {
      id.value = `column-${props._uid}`
    })

    watch(
      childrenData,
      (value) => {
        props.updateColumn(value, props.columnIndex)
      },
      { deep: true },
    )

    return {
      childrenData,
      showEmptyState,
      hover,
      id,
      rootStyles,
      containerStyles,
      contentStyles,
      columnWidth,
      innerWidth,
      addTitle,
      addText,
      addDivider,
      addImage,
      addButton,
      addHtml,
      addSignature,
      removeContent,
      builderElementStyles,
    }
  },
})
</script>

<style lang="scss" scoped>
.email-template__content-container {
  &:empty {
    min-height: 30px;
  }
}
.ak-attached-actions {
  &--hover {
    .ak-attached-actions__item--overlap {
      top: 100%;

      transition: opacity 0.2s ease;
      will-change: opacity;
      opacity: 0;
    }
    &:hover {
      z-index: 5;
      .ak-attached-actions__item--overlap {
        opacity: 1;
      }
    }
    > div {
      > .ak-attached-actions__item {
        transition: opacity 0.2s ease;
        will-change: opacity;
        opacity: 0;
      }
      &:hover {
        z-index: 99;
        > .ak-attached-actions__item {
          opacity: 1;
        }
      }
    }
  }
}
</style>
